import AButton from '@/components/A/AButton/AButton'
import AInput from '@/components/A/AInput/AInput'
import AModal from '@/components/A/AModal/AModal'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Divider, message } from 'antd'
import styles from './AddMemberToWorkspaceModal.module.less'
import { useState } from 'react'
import { commonUtils } from '@/helpers/commonUtils'
import cls from 'classnames'
import { api } from '@/api'
import { useRequest } from 'alova/client'
import { useTranslation } from 'react-i18next'

interface Props {
  boundCount: number
  baseSeatCount: number
  onConfirm?: (email: string) => void
  onCancel?: () => void
  contactUs?: () => void
}

function AddMemberToWorkspaceModal(props: Props) {
  const { t } = useTranslation()
  const modal = useModal()
  const [email, setEmail] = useState<string>('')
  const [workspaceIsFull, setWorkspaceIsFull] = useState(props.boundCount >= props.baseSeatCount)
  const { send: sendInvitation, loading } = useRequest(api.staff.invite, { immediate: false })

  const handleConfirm = async () => {
    if (workspaceIsFull) {
      message.warning(t('dashboard.accountPage.message.reachedTheCeiling'))
      return
    }
    if (!commonUtils.emailFormatValidator(email) || email === '') {
      message.warning(t('dashboard.accountPage.message.incorrectMailboxFormat'))
      return
    }
    try {
      await sendInvitation(email)
      props.onConfirm?.(email)
      modal.hide()
      setEmail('')
    } catch (e) {
      const error = e as ApiError
      if (error.statusCode === 40004) {
        setWorkspaceIsFull(true)
      } else if (error.statusCode === 40005) {
        message.error(t('dashboard.accountPage.message.notAdminOperate'))
      } else {
        message.error(error.statusMessage)
      }
    }
  }

  const handleContactUsClick = () => {
    props.contactUs?.()
    modal.hide()
  }

  return (
    <AModal
      testId='account-add-member-modal'
      open={modal.visible}
      className={styles.addMemberToWorkspaceModal}
      title={t('dashboard.accountPage.message.addMemberToWorkspace')}
      footer={null}
      onCancel={() => {
        props.onCancel?.()
        modal.hide()
        setEmail('')
      }}
      afterClose={() => {
        modal.remove()
      }}
    >
      <div className={styles.emailBox}>
        <span className={cls({ [styles.error]: workspaceIsFull })}>
          <AInput
            testId='account-add-member-email-input'
            className={styles.inputBox}
            placeholder={t('dashboard.accountPage.message.emailAddress')}
            onChange={(e) => {
              setEmail(e.target.value)
            }}
          />
        </span>
        <AButton testId='account-add-member-send-btn' type='primary' onClick={handleConfirm} loading={loading}>
          {t('dashboard.accountPage.message.send')}
        </AButton>
      </div>
      {workspaceIsFull ? (
        <p className={cls(styles.desc, styles.error)}>
          {t('dashboard.accountPage.message.yourTeamIsFull')}
        </p>
      ) : (
        <p className={styles.desc}>
          {t('dashboard.accountPage.message.willSendAnInvitationEmailToThisAddressForConfirmation')}
        </p>
      )}
      <Divider />
      <div className={styles.footerDesc}>
        <span className={styles.black}>
          {t('dashboard.accountPage.message.workspaceMemberShip')}: {props.boundCount} /{' '}
          {props.baseSeatCount}
        </span>
        <Divider type='vertical' />
        <span className={styles.blue} onClick={handleContactUsClick} data-testid='account-add-member-contact-us-btn'>
          {t('dashboard.accountPage.message.contactUs')}
        </span>
        {t('dashboard.accountPage.message.forMoreMembership')}
      </div>
    </AModal>
  )
}

export default NiceModal.create(AddMemberToWorkspaceModal)
